import { fabric } from 'fabric';
import { create } from 'zustand';

interface State {
  canvas: fabric.Canvas | null;
  activeObject: { ref: fabric.Object | null };
  undoStackCount: number;
  redoStackCount: number;
  domLoadTime?: number;
  fabricTextareaIsVisible: boolean;
  advancedTextEditingEnabled: boolean | undefined;
  showAdvancedTextEditingMetrics: boolean;
  showCursorPosition: boolean;
  wizardStageOverride: 1 | 2 | 3 | null;
  templateIdOverride: string | null;
  setCanvas: (canvas: fabric.Canvas) => void;
  setDomLoadTime: (domLoadTime: number) => void;
  updateActiveObject: () => void;
  toggleFabricTextareaVisibility: () => void;
  toggleAdvancedTextEditingEnabled: () => void;
  toggleShowCursorPosition: () => void;
  setWizardStageOverride: (stage: 1 | 2 | 3 | null) => void;
  setTemplateIdOverride: (templateId: string | null) => void;
  toggleShowAdvancedTextEditingMetrics: () => void;
}

export const useDebugStore = create<State>((set, get) => ({
  canvas: null,
  activeObject: { ref: null },
  undoStackCount: 0,
  redoStackCount: 0,
  fabricTextareaIsVisible: false,
  advancedTextEditingEnabled: undefined,
  showAdvancedTextEditingMetrics: false,
  showCursorPosition: false,
  wizardStageOverride: null,
  templateIdOverride: null,
  setCanvas: (canvas: fabric.Canvas): void => {
    ['selection:created', 'selection:updated', 'selection:cleared'].forEach(
      (eventName) => {
        canvas.on(eventName, () => {
          const object = canvas.getActiveObject() ?? null;
          set({ activeObject: { ref: object } });
        });
      }
    );

    set({ canvas });
  },

  updateActiveObject: (): void => {
    set({ activeObject: { ref: get().canvas?.getActiveObject() ?? null } });
  },
  setDomLoadTime: (domLoadTime: number): void => {
    set({ domLoadTime });
  },
  toggleFabricTextareaVisibility: (): void => {
    set({ fabricTextareaIsVisible: !get().fabricTextareaIsVisible });
  },
  toggleAdvancedTextEditingEnabled: (): void => {
    // On the initial call, advancedTextEditingEnabled is undefined, so we treat
    // it as false, because we assume the button is used to disable the feature
    // when it's enabled.
    if (get().advancedTextEditingEnabled === undefined) {
      set({
        advancedTextEditingEnabled: false,
      });

      return;
    }

    set({
      advancedTextEditingEnabled: !get().advancedTextEditingEnabled,
    });
  },
  toggleShowAdvancedTextEditingMetrics: (): void => {
    set({
      showAdvancedTextEditingMetrics: !get().showAdvancedTextEditingMetrics,
    });
  },
  toggleShowCursorPosition: (): void => {
    set({ showCursorPosition: !get().showCursorPosition });
  },
  setWizardStageOverride: (stage: 1 | 2 | 3 | null): void => {
    set({ wizardStageOverride: stage });
  },
  setTemplateIdOverride: (templateId: string | null): void => {
    set({ templateIdOverride: templateId });
  },
}));

export const debugStoreSelector = {
  canvas: (state: State): fabric.Canvas | null => state.canvas,
  setCanvas: (state: State): ((canvas: fabric.Canvas) => void) =>
    state.setCanvas,
  activeObject: (state: State): { ref: fabric.Object | null } =>
    state.activeObject,
  undoStackCount: (state: State): number => state.undoStackCount,
  redoStackCount: (state: State): number => state.redoStackCount,
  fabricTextareaIsVisible: (state: State): State['fabricTextareaIsVisible'] =>
    state.fabricTextareaIsVisible,
  toggleFabricTextareaVisibility: (
    state: State
  ): State['toggleFabricTextareaVisibility'] =>
    state.toggleFabricTextareaVisibility,
  advancedTextEditingEnabled: (
    state: State
  ): State['advancedTextEditingEnabled'] => state.advancedTextEditingEnabled,
  toggleAdvancedTextEditingEnabled: (
    state: State
  ): State['toggleAdvancedTextEditingEnabled'] =>
    state.toggleAdvancedTextEditingEnabled,
  showAdvancedTextEditingMetrics: (
    state: State
  ): State['showAdvancedTextEditingMetrics'] =>
    state.showAdvancedTextEditingMetrics,
  toggleShowAdvancedTextEditingMetrics: (
    state: State
  ): State['toggleShowAdvancedTextEditingMetrics'] =>
    state.toggleShowAdvancedTextEditingMetrics,
  showCursorPosition: (state: State): boolean => state.showCursorPosition,
  toggleShowCursorPosition: (state: State): State['toggleShowCursorPosition'] =>
    state.toggleShowCursorPosition,
  wizardStageOverride: (state: State): State['wizardStageOverride'] =>
    state.wizardStageOverride,
  setWizardStageOverride: (state: State): State['setWizardStageOverride'] =>
    state.setWizardStageOverride,
  templateIdOverride: (state: State): State['templateIdOverride'] =>
    state.templateIdOverride,
  setTemplateIdOverride: (state: State): State['setTemplateIdOverride'] =>
    state.setTemplateIdOverride,
};
